import React, { forwardRef, useEffect, useRef, useState } from "react";
import { Input } from "@/v2-components/ui/input";
import { useTrans } from "@/utils/useTrans";
import SearchIcon from "@/v2-components/core/icon/search-icon";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/v2-components/ui/dialog";
import CarotLeftIcon from "@/v2-components/core/icon/carot-left-icon";
import { cn } from "@/libs/utils";
import { GamesSearchPreview } from "@/v2-components/pages/games/search/GamesSearchPreview";
import { useRouter } from "next/router";
import { useGameKeywordStore } from "@/store/games-recent-keywords-store";
import { useBhapticsContents } from "@/hooks/useBhapticsContents";
import { createZustandContext } from "@/store/context/createZustandContext";
import TriangleDownIcon from "@/v2-components/core/icon/triangle-down-icon";
import { GamesSearchResultPreview } from "@/v2-components/pages/games/search/GamesSearchResultPreview";
import CloseIcon from "@/v2-components/core/icon/close-icon";

interface GamesSearchStore {
  searchText: string;
  setSearchText: (v: string) => void;
}
/*
 game 검색어 store
 */
export const {
  Provider: GamesSearchStoreProvider,
  useStore: useGamesSearchStore,
} = createZustandContext<GamesSearchStore>((set) => ({
  searchText: "",
  setSearchText: (text) => set({ searchText: text }),
}));

export const GamesSearchInput = () => {
  return (
    <GamesSearchStoreProvider>
      <Mobile />
      <DeskTop />
    </GamesSearchStoreProvider>
  );
};

// mobile input click 시 full screen dialog
const Mobile = () => {
  const { t } = useTrans(["games"]);
  const [openDialog, setOpenDialog] = useState(false);
  const { inputRef, searchText, setSearchText, handleSearch } =
    useSearchHandler({ onSearch: () => setOpenDialog(false) });

  const blur = (e: Event) => {
    e.preventDefault();
    requestAnimationFrame(() => {
      inputRef.current?.blur();
    });
  };

  const focus = (e: Event) => {
    e.preventDefault();
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  const handleClosePreview = () => {
    return setOpenDialog(false);
  };

  const isSearching = openDialog && searchText;
  const isJustOpenSearch = openDialog && !searchText;

  return (
    <div className="flex-1 md:hidden">
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogHeader className="hidden">
          <DialogTitle></DialogTitle>
        </DialogHeader>
        <DialogTrigger asChild>
          {/* 모양만  input */}
          <div className="flex w-full items-center justify-between rounded border border-coolgray-20 bg-white px-4 py-[14px]">
            {searchText ? (
              <p className="text-coolgray-90"> {searchText}</p>
            ) : (
              <p className="flex-1 text-base text-coolgray-30">
                {t("Search for Supported Games")}
              </p>
            )}
            <SearchIcon className="ml-[10px] size-5 flex-shrink-0" />
          </div>
        </DialogTrigger>

        <DialogContent
          onCloseAutoFocus={blur}
          onOpenAutoFocus={focus}
          fullScreen
        >
          {/* header search bar  */}
          <div className="flex items-center gap-4 border-b px-4 py-3">
            <DialogClose asChild>
              <button>
                <CarotLeftIcon />
              </button>
            </DialogClose>
            <MobileInput
              ref={inputRef}
              searchText={searchText}
              setSearchText={setSearchText}
              onSearch={handleSearch}
              placeholder={t("Search for Supported Games")}
            />
          </div>

          {/* 검색결과미리보기 ,  검색미리보기*/}
          <section className="w-full flex-1 overflow-y-auto py-4">
            {isSearching && <GamesSearchResultPreview />}
            {isJustOpenSearch && (
              <GamesSearchPreview onClose={handleClosePreview} />
            )}
          </section>
        </DialogContent>
      </Dialog>
    </div>
  );
};

type MobileInputProps = {
  inputRef?: React.Ref<HTMLInputElement>;
  searchText: string;
  setSearchText: (v: string) => void;
  placeholder: string;
  onClick?: () => void;
  onKeyDown?: () => void;
  onSearch?: () => void;
  type?: "search" | "searchPreview";
  className?: string;
  inputClassName?: string;
};

/*
  Mobile Input
 */
export const MobileInput = forwardRef<HTMLInputElement, MobileInputProps>(
  (
    {
      searchText,
      setSearchText,
      placeholder,
      onClick,
      onKeyDown,
      onSearch,
      type,
      className,
      inputClassName,
    },
    ref,
  ) => {
    const router = useRouter();
    const querySearch =
      typeof router.query.search === "string" ? router.query.search : "";
    const { setSearch } = useBhapticsContents();

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        onSearch?.();
      }
      if (e.key !== "Escape") {
        onKeyDown?.();
      }
    };

    //렌더시 searchText init
    useEffect(() => {
      setSearchText?.(querySearch);
      setSearch(querySearch);
    }, [querySearch]);

    return (
      <label
        onClick={onClick}
        className={cn(
          "relative flex w-full items-center md:w-[360px]",
          className,
        )}
      >
        {/* input */}
        <Input
          ref={ref}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className={cn("w-full pr-14", inputClassName)}
          placeholder={placeholder}
          onKeyDown={handleKeyDown}
        />

        {searchText && (
          <button
            type="button"
            className="absolute right-12 top-1/2 flex -translate-y-1/2 items-center"
            aria-label="Clear search"
            onClick={() => setSearchText("")}
            tabIndex={0}
          >
            <CloseIcon className="size-6 text-coolgray-90" />
          </button>
        )}
        {/* Divider and Icons */}
        <div className="absolute right-4 top-1/2 flex -translate-y-1/2 transform items-center gap-2">
          <button type={"button"} onClick={onSearch}>
            <SearchIcon className="ml-[10px] size-5" />
          </button>
        </div>
      </label>
    );
  },
);
MobileInput.displayName = "MobileInput";

export const DeskTop = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const { t } = useTrans(["games"]);
  const { inputRef, searchText, setSearchText, handleSearch } =
    useSearchHandler({ onSearch: () => setOpen(false) });

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
    if (e.key === "Escape") {
      return setOpen(false);
    }
    setOpen(true);
  };

  return (
    <div
      ref={containerRef}
      className="relative hidden w-full max-w-[360px] flex-col md:flex md:w-[360px]"
    >
      <DesktopInput
        ref={inputRef}
        searchText={searchText}
        setSearchText={setSearchText}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        onSearch={handleSearch}
        open={open}
        placeholder={t("Search for Supported Games")}
      />
      {open && (
        <Outer className="flex h-auto max-h-[calc(65vh)] flex-col overflow-hidden">
          <div className="flex-1 overflow-auto">
            {!searchText && (
              <GamesSearchPreview
                onClose={() => setOpen(false)}
                className="border-t border-coolgray-20 py-4"
              />
            )}
            {!!searchText && (
              <GamesSearchResultPreview
                onClick={() => setOpen(false)}
                className="max-h-[288px] overflow-y-auto border-t border-coolgray-20"
              />
            )}
          </div>
          <Close onClick={() => setOpen(false)}>{t("Close")}</Close>
        </Outer>
      )}
    </div>
  );
};

const Close = ({
  onClick,
  children,
}: React.PropsWithChildren<{ onClick: (e: any) => void }>) => (
  <section className="flex justify-end border-t border-coolgray-20 px-4 py-4 text-sm font-600 text-coolgray-50">
    <button onClick={onClick}>{children}</button>
  </section>
);

const Outer = ({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) => (
  <div
    className={cn(
      "absolute top-full z-[99] w-full rounded rounded-t-none bg-white text-sm text-coolgray-80",
      "border-t-none border-x border-b",
      "border-x-blue-60 border-b-blue-60",
      className,
    )}
  >
    {children}
  </div>
);

type DesktopInputProps = {
  searchText: string;
  setSearchText: (v: string) => void;
  placeholder: string;
  onFocus: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onSearch: () => void;
  open: boolean;
};

/*
 Desktop Input
 */
export const DesktopInput = forwardRef<HTMLInputElement, DesktopInputProps>(
  (
    {
      searchText,
      setSearchText,
      onFocus,
      onKeyDown,
      onSearch,
      open,
      placeholder,
    },
    ref,
  ) => {
    const router = useRouter();

    const querySearch =
      typeof router.query.search === "string" ? router.query.search : "";
    const { setSearch } = useBhapticsContents();

    //렌더시 searchText init
    useEffect(() => {
      setSearchText?.(querySearch);
      setSearch(querySearch);
    }, [querySearch]);
    return (
      <div
        className={cn(
          "flex w-full items-center rounded border px-4",
          open
            ? [
                "border-b-0 border-blue-60 border-x-blue-60 border-t-blue-60",
                "rounded-b-none rounded-t",
              ]
            : "border-coolgray-20",
        )}
      >
        <div className={cn("m-[1px] flex w-full justify-between")}>
          <input
            ref={ref}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            className={cn(
              "h-[48px] flex-1 py-2 text-sm outline-none",
              open && "border-1 border-b-coolgray-20",
            )}
          />

          {searchText && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={(e) => {
                // 열린 상태에서 클리어 버튼 누르는 경우에
                // 포커스를 유지하고 이벤트 버블링을 방지합니다.
                if (open) {
                  e.stopPropagation();
                  (
                    ref as React.RefObject<HTMLInputElement | null>
                  )?.current?.focus?.();
                }
                setSearchText("");
              }}
              tabIndex={0}
            >
              <CloseIcon className="size-6 text-coolgray-90" />
            </button>
          )}
          <div className="ml-2 flex items-center gap-2">
            <TriangleDownIcon
              className={cn(
                "size-6 transition-transform duration-200",
                open && "rotate-180",
              )}
            />
            <div className="h-5 w-px bg-coolgray-20" />
            <button type={"button"} onClick={onSearch}>
              <SearchIcon className="ml-[10px] size-5" />
            </button>
          </div>
        </div>
      </div>
    );
  },
);

DesktopInput.displayName = "DesktopInput";

/*
 search hooks
 */
export const useSearchHandler = ({ onSearch }: { onSearch?: () => void }) => {
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);

  const searchText = useGamesSearchStore((s) => s.searchText);
  const setSearchText = useGamesSearchStore((s) => s.setSearchText);
  const { addKeyword } = useGameKeywordStore();
  const { setSearch } = useBhapticsContents();

  const handleSearch = async () => {
    const searchValue = searchText.trim();

    await router.replace(
      {
        pathname: "/games/search",
        query: {
          ...router.query,
          search: searchValue,
        },
      },
      undefined,
    );

    if (searchValue) {
      addKeyword(searchValue);
    }

    setSearch(searchValue);
    onSearch?.();
  };

  return {
    inputRef,
    searchText,
    setSearchText,
    handleSearch,
  };
};
